import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { num, pcs, pct, fmtDate } from "@/lib/format";
import PeriodPicker, { todayWib, weekRange } from "@/components/PeriodPicker";
import RepairTickets from "@/components/RepairTickets";
import TrxActions from "@/components/TrxActions";
import { PageTitle, Panel, Stat, Pill } from "@/components/common";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Production() {
  const [tab, setTab] = useState("AMBIL");
  const [pv, setPv] = useState({ period: "BULANAN", date: todayWib() });
  const [items, setItems] = useState([]);
  const [wip, setWip] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ status: "CONFIRMED", page_size: tab === "REJECT" ? 500 : 100 });
    if (tab !== "WIP" && tab !== "REJECT") p.append("transaction_type", tab);
    if (pv.period === "BULANAN") p.append("month", pv.date.slice(0, 7));
    else if (pv.period === "HARIAN") { p.append("date_from", pv.date); p.append("date_to", pv.date); }
    else { const [a, b] = weekRange(pv.date); p.append("date_from", a); p.append("date_to", b); }
    api.get(`/transactions?${p.toString()}`).then((r) => {
      let its = r.data.items;
      if (tab === "REJECT") its = its.filter((x) => ["SETOR", "RIJEK"].includes(x.transaction_type) && (((x.reject_quantity || 0) > 0) || ((x.repair_quantity || 0) > 0)));
      setItems(its);
    });
    api.get("/wip").then((r) => setWip(r.data));
  }, [tab, pv]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageTitle title="Produksi" desc="Log jahitan keluar (Ambil), jahitan masuk (Setor), open WIP & rijek (afkir vs bisa diperbaiki)."
        right={
          <PeriodPicker value={pv} onChange={setPv} testId="production-period" />
        } />

      {wip && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <Panel><Stat label="Total Ambil (Confirmed)" value={pcs(wip.total_ambil)} tone="blue" /></Panel>
          <Panel><Stat label="Total Setor (Confirmed)" value={pcs(wip.total_setor)} tone="emerald" /></Panel>
          <Panel><Stat label="Open WIP" value={pcs(wip.open_wip)} tone="amber" /></Panel>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="AMBIL" data-testid="prod-tab-ambil">Ambil</TabsTrigger>
          <TabsTrigger value="SETOR" data-testid="prod-tab-setor">Setor</TabsTrigger>
          <TabsTrigger value="REJECT" data-testid="prod-tab-reject">Reject</TabsTrigger>
        </TabsList>
      </Tabs>

      {(tab === "AMBIL" || tab === "REJECT") && <div className="mb-4"><RepairTickets /></div>}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
              {(tab === "REJECT"
                ? ["Tanggal", "Worker", "Produk", "Sumber", "Afkir", "Bisa Diperbaiki", "Status", ""]
                : ["Tanggal", "Worker", "Job", "Produk", "Qty", "Good", "Reject", "Status", ""]
              ).map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((t) => tab === "REJECT" ? (
              <tr key={t.transaction_id} className="border-b border-slate-100 hover:bg-slate-50/80" data-testid={`reject-row-${t.transaction_id}`}>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(t.transaction_date)}</td>
                <td className="px-3 py-2.5"><span className="font-semibold text-slate-800">{t.worker_id}</span> <span className="text-slate-500">{t.worker_name}</span></td>
                <td className="px-3 py-2.5 text-slate-700">{t.product_name || "-"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">{t.transaction_type === "RIJEK" ? "Rijek Sortir" : "Setor"}</td>
                <td className="px-3 py-2.5 text-right">{(t.reject_quantity || 0) > 0 ? <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700" data-testid={`reject-afkir-${t.transaction_id}`}>{num(t.reject_quantity)} afkir</span> : <span className="text-slate-300">-</span>}</td>
                <td className="px-3 py-2.5 text-right">{(t.repair_quantity || 0) > 0 ? <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700" data-testid={`reject-repair-${t.transaction_id}`}>{num(t.repair_quantity)} perbaiki</span> : <span className="text-slate-300">-</span>}</td>
                <td className="px-3 py-2.5"><Pill value={t.status} /></td>
                <td className="px-3 py-2.5"><TrxActions t={t} onDone={load} /></td>
              </tr>
            ) : (
              <tr key={t.transaction_id} className="border-b border-slate-100 hover:bg-slate-50/80">
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(t.transaction_date)}</td>
                <td className="px-3 py-2.5"><span className="font-semibold text-slate-800">{t.worker_id}</span> <span className="text-slate-500">{t.worker_name}</span></td>
                <td className="px-3 py-2.5 mono text-xs text-slate-400">{t.job_id || "-"}</td>
                <td className="px-3 py-2.5 text-slate-700">{t.product_name || "-"}</td>
                <td className="px-3 py-2.5 mono text-right">{num(t.quantity)}</td>
                <td className="px-3 py-2.5 mono text-right text-emerald-700">{t.transaction_type === "SETOR" ? num(t.good_quantity) : "-"}</td>
                <td className="px-3 py-2.5 mono text-right text-rose-600">{t.reject_quantity ? num(t.reject_quantity) : "-"}</td>
                <td className="px-3 py-2.5"><Pill value={t.status} /></td>
                <td className="px-3 py-2.5"><TrxActions t={t} onDone={load} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-400">Tidak ada data.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
